import apiClient from './client';

// ===== Crawl History API =====

export const getCrawlHistory = async (params: {
  page?: number;
  pageSize?: number;
  sport?: string;
  requestType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{
  status: string;
  data: Array<{
    history_id: number;
    request_type: string;
    sport_name: string;
    league_code: string | null;
    crawl_date: string | null;
    ai_provider: string | null;
    admin_id: number | null;
    notion_admin_id: number | null;
    admin_name: string | null;
    notion_admin_name: string | null;
    execution_start: string;
    execution_end: string | null;
    duration: number | null;  // 실행 시간 (초)
    status: string;
    total_matches: number;
    success_count: number;
    error_count: number;
    error_message: string | null;
    source: string | null;
    created_by: string | null;
    created_at: string;
    match_count: number;
    created_count: number;
    updated_count: number;
    failed_count: number;
  }>;
  pagination: {
    current_page: number;
    total_pages: number;
    page_size: number;
    total_items: number;
    has_next: boolean;
    has_prev: boolean;
  };
}> => {
  const queryParams: any = {
    page: params.page || 1,
    page_size: params.pageSize || 20
  };

  if (params.sport) queryParams.sport = params.sport;
  if (params.requestType) queryParams.request_type = params.requestType;
  if (params.status) queryParams.status = params.status;
  if (params.startDate) queryParams.start_date = params.startDate;
  if (params.endDate) queryParams.end_date = params.endDate;

  const response = await apiClient.get('/api/crawl/history', { params: queryParams });
  return response.data;
};

export const getCrawlHistoryDetail = async (historyId: number): Promise<{
  status: string;
  data: {
    history: {
      history_id: number;
      request_type: string;
      sport_name: string;
      league_code: string | null;
      crawl_date: string | null;
      ai_provider: string | null;
      admin_id: number | null;
      notion_admin_id: number | null;
      admin_name: string | null;
      notion_admin_name: string | null;
      execution_start: string;
      execution_end: string | null;
      duration: number | null;  // 실행 시간 (초)
      status: string;
      total_matches: number;
      success_count: number;
      error_count: number;
      error_message: string | null;
      source: string | null;
      created_by: string | null;
      created_at: string;
    };
    match_results: Array<{
      result_id: number;
      history_id: number;
      game_id: string;
      home_team: string | null;
      away_team: string | null;
      action_type: string;
      db_saved: boolean;
      prediction_saved: boolean;
      notion_saved: boolean;
      error_message: string | null;
      created_at: string;
    }>;
  };
}> => {
  const response = await apiClient.get(`/api/crawl/history/${historyId}`);
  return response.data;
};

export const getCrawlStats = async (sport?: string, days: number = 7): Promise<{
  status: string;
  sport: string;
  days: number;
  stats: {
    total_crawls: number;
    success_crawls: number;
    failed_crawls: number;
    total_matches: number;
    avg_duration: number;  // 평균 실행 시간 (초)
    unique_dates: number;
    unique_leagues: number;
  };
}> => {
  const params: any = { days };
  if (sport) params.sport = sport;
  const response = await apiClient.get('/api/crawl/history/stats', { params });
  return response.data;
};

// ===== Psynet History API =====

export const getPsynetHistory = async (params: {
  page?: number;
  page_size?: number;
  sport?: string;
  status?: string;
  match_date?: string;
}): Promise<{
  status: string;
  data: Array<{
    history_id: number;
    match_id: number;
    prediction_id: number | null;
    publish_status: string;
    fail_reason: string | null;
    published_at: string | null;
    created_at: string | null;
    match_info: {
      match_date: string;
      match_time: string;
      home_team: string;
      away_team: string;
      league_name: string;
      sport_name: string;
      sport_name_kr: string | null;
    };
  }>;
  pagination: {
    page: number;
    page_size: number;
    total_count: number;
    total_pages: number;
  };
}> => {
  const queryParams: any = {
    page: params.page || 1,
    page_size: params.page_size || 20
  };

  if (params.sport) queryParams.sport = params.sport;
  if (params.status) queryParams.status = params.status;
  if (params.match_date) queryParams.match_date = params.match_date;

  const response = await apiClient.get('/api/psynet/history', { params: queryParams });
  return response.data;
};

export const generateTestCrawlHistory = async (): Promise<{
  status: string;
  message: string;
  generated_count: number;
}> => {
  const response = await apiClient.post('/api/crawl/history/generate-test-data');
  return response.data;
};

// ===== Psynet Auto Publish API =====

export const publishToPsynet = async (
  matchId: number,
  force: boolean = false
): Promise<{
  status: string;
  message: string;
  match_id: number;
  sport: string;
  league: string;
  home_team: string;
  away_team: string;
  prediction_id: number;
  ai_model: string;
}> => {
  const response = await apiClient.post('/api/psynet/publish', {
    match_id: matchId,
    force: force
  });
  return response.data;
};

// Psynet Publish by Date API
export const publishByDateToPsynet = async (
  matchDate: string,
  sport?: string,
  adminId?: number
): Promise<{
  status: string;
  match_date: string;
  total: number;
  results: Array<{
    match_id: number;
    status: string;
    home_team: string;
    away_team: string;
    sport?: string;
    league?: string;
    prediction_id?: number;
    message?: string;
    fail_reason?: string;
  }>;
  summary: {
    success: number;
    already_published: number;
    skipped: number;
    failed: number;
    error: number;
  };
}> => {
  const payload: { match_date: string; sport?: string; admin_id?: number } = {
    match_date: matchDate
  };
  if (sport) {
    payload.sport = sport;
  }
  if (adminId) {
    payload.admin_id = adminId;
  }
  const response = await apiClient.post('/api/psynet/publish-by-date', payload);
  return response.data;
};
